/**
 * Pure helpers for the screen-print JOB PACKET (docs/screen-print-station-audit-and-plan-v1.md § 4.2,
 * phase P3): what a run's setups look like once each design's screen spec is joined in, the honest
 * screen count (F1), the ring for the whole run, and the small lookups the mockup card needs.
 */
import type { DesignSummary } from './design-lib';
import {
  DEFAULT_LOAD_RULE,
  type DesignSize,
  fallbackBox,
  findPlacementRule,
  fmtIn,
  type InchBox,
  type PlacementRule,
  type Platen,
  placeDesign,
  SEED_PLACEMENT_RULES,
  viewerSign,
} from './press-guide-lib';
import type { RunSetup, ScreenPrintWorkItem } from './screen-print-lib';
import type {
  DesignScreenPrintSpec,
  PressShape,
  PressSummary,
  RingScreen,
  ScreenSpec,
} from './screen-print-spec-lib';

/** A setup (design · location · band) with its design record and spec joined, when known. */
export type SetupWithSpec = RunSetup & {
  design?: DesignSummary;
  spec?: DesignScreenPrintSpec;
  /** The spec's screens in print order — head first when set, else seq. */
  screens: ScreenSpec[];
};

export function joinSpecs(
  setups: readonly RunSetup[],
  designs: ReadonlyMap<string, DesignSummary>,
): SetupWithSpec[] {
  return setups.map((s) => {
    const design = s.designId ? designs.get(s.designId) : undefined;
    const spec = design?.screenPrintSpec;
    const screens = spec ? [...spec.screens].sort(bySeq) : [];
    return { ...s, design, spec, screens };
  });
}

const bySeq = (a: ScreenSpec, b: ScreenSpec) => a.seq - b.seq;

/**
 * The run's screen count (F1). A setup IS a screen only for one-colour art; with specs joined the
 * count is Σ colours per setup. `undefined` while ANY setup's design lacks a spec — the header then
 * says "N setups · colours not set" rather than a number that is wrong 3× on multi-colour art.
 */
export function runScreenCount(setups: readonly SetupWithSpec[]): number | undefined {
  if (setups.length === 0) return 0;
  if (setups.some((s) => !s.spec)) return undefined;
  return setups.reduce((n, s) => n + s.screens.length, 0);
}

/** Designs on the run with no spec — the amber strip names them. */
export function setupsMissingSpec(setups: readonly SetupWithSpec[]): SetupWithSpec[] {
  const seen = new Set<string>();
  return setups.filter((s) => {
    if (s.spec) return false;
    const key = s.designId ?? s.designName ?? s.key;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Every colour on the run as the ring sees it — labelled `<design> · <colour>` so a conflict names both. */
export function ringScreensForRun(setups: readonly SetupWithSpec[]): RingScreen[] {
  const out: RingScreen[] = [];
  for (const s of setups) {
    for (const c of s.screens) {
      out.push({
        label: `${s.designName ?? s.designId ?? 'design'} · ${c.name}`,
        ...(c.head != null ? { head: c.head } : {}),
        ...(c.hex ? { hex: c.hex } : {}),
        ...(c.meshCount != null ? { meshCount: c.meshCount } : {}),
      });
    }
  }
  return out;
}

/** The shop's automatic, drawn when no press is registered yet (D17). */
export const FALLBACK_PRESS: PressShape & { name: string } = {
  name: 'M&R 12/10',
  kind: 'automatic',
  stations: 12,
  printHeads: 10,
  flashStations: [2, 8],
};

/**
 * The press a run is drawn against: the first spec that names one, else the shop default, else the
 * first registered, else the M&R shape. Same rule the spec editor uses, so the two never disagree.
 */
export function resolveRunPress(
  presses: readonly PressSummary[],
  setups: readonly SetupWithSpec[],
): PressShape & { name: string; pressId?: string } {
  const named = setups.map((s) => s.spec?.pressId).find((id): id is string => Boolean(id));
  const chosen =
    (named ? presses.find((p) => p.pressId === named) : undefined) ??
    presses.find((p) => p.isDefault) ??
    presses[0];
  return chosen ?? FALLBACK_PRESS;
}

/** Distinct (store, product) pairs on the run — the mockup card fetches one image per product. */
export function distinctProducts(
  work: readonly ScreenPrintWorkItem[],
): { storeId: string; productId: string; title: string }[] {
  const seen = new Map<string, { storeId: string; productId: string; title: string }>();
  for (const w of work) {
    const productId = (w as { productId?: string }).productId;
    if (!productId) continue;
    const key = `${w.storeId}#${productId}`;
    if (!seen.has(key)) seen.set(key, { storeId: w.storeId, productId, title: w.title });
  }
  return [...seen.values()];
}

export const productKey = (storeId: string, productId: string) => `${storeId}#${productId}`;

/** The bare `ssSku` of the run's first resolved blank — the art-on-blank card's photo. */
export function firstBlankSku(work: readonly ScreenPrintWorkItem[]): string | undefined {
  const w = work.find((x) => x.resolvedBlankSku);
  const raw = w?.resolvedBlankSku;
  if (!raw) return undefined;
  return raw.startsWith('BLANK#') ? raw.slice('BLANK#'.length) : raw;
}

/** "13.5 × 16 in" from a band's derived print inches, or undefined when the file is not READY. */
export function printInchesLabel(
  design: DesignSummary | undefined,
  band: 'adult' | 'youth',
): string | undefined {
  const file = band === 'youth' ? design?.youth : design?.adult;
  if (!file || file.printWidthIn == null || file.printHeightIn == null) return undefined;
  return `${trim(file.printWidthIn)} × ${trim(file.printHeightIn)} in`;
}
const trim = (n: number) => n.toFixed(2).replace(/\.?0+$/, '');

/** Cure + notes per design, de-duplicated — the Ink & cure card. */
export function cureLines(
  setups: readonly SetupWithSpec[],
): { design: string; cure?: string; notes?: string }[] {
  const seen = new Set<string>();
  const out: { design: string; cure?: string; notes?: string }[] = [];
  for (const s of setups) {
    if (!s.spec) continue;
    const key = s.designId ?? s.designName ?? s.key;
    if (seen.has(key)) continue;
    seen.add(key);
    const cure =
      s.spec.cureTempF != null || s.spec.cureSeconds != null
        ? [
            s.spec.cureTempF != null ? `${s.spec.cureTempF} °F` : null,
            s.spec.cureSeconds != null ? `${s.spec.cureSeconds} s` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : undefined;
    out.push({ design: s.designName ?? s.designId ?? 'design', cure, notes: s.spec.pressNotes });
  }
  return out;
}

// ── Placement pictures: the art on the blank's photo, and the art on the pallet ─────────────────

/** The band's derived print size, when the file is READY. */
export function printSizeOf(
  design: DesignSummary | undefined,
  band: 'adult' | 'youth',
): DesignSize | undefined {
  const file = band === 'youth' ? design?.youth : design?.adult;
  if (!file || file.printWidthIn == null || file.printHeightIn == null) return undefined;
  return { widthIn: file.printWidthIn, heightIn: file.printHeightIn };
}

/**
 * How the "art on blank" card is drawn (§ 3.4): the design preview laid over the S&S colourway photo at
 * roughly true proportion. Approximate BY CONSTRUCTION — a product photo has no scale — so the numbers
 * here are a typical S&S front shot: the garment body spans about 72 % of the frame and the collar
 * seam sits about 14 % down; a body is ~22 in across on an adult, ~16 in on a youth. The card's caption
 * says "placement approximate — the guides are on the press", and nothing downstream reads this.
 */
export const ART_PHOTO = {
  bodyWidthPct: 72,
  collarTopPct: 14,
  bodyWidthIn: { adult: 22, youth: 16 },
  /** The card's width : height, so a vertical inch is scaled by 6/7 of a horizontal one. */
  aspect: 6 / 7,
} as const;

export type ArtOverlay = { leftPct: number; topPct: number; widthPct: number; guessed: boolean };

export function artOverlay(
  location: string,
  band: 'adult' | 'youth',
  size: DesignSize | undefined,
): ArtOverlay {
  const rule = findPlacementRule(SEED_PLACEMENT_RULES, location, band);
  if (!size || !rule) {
    // No size or no rule for this location: a centred block, honest about being a guess.
    return { leftPct: 30, topPct: 30, widthPct: 40, guessed: true };
  }
  const hPctPerIn = ART_PHOTO.bodyWidthPct / ART_PHOTO.bodyWidthIn[band];
  const vPctPerIn = hPctPerIn * ART_PHOTO.aspect;
  const widthPct = Math.min(80, size.widthIn * hPctPerIn);
  const heightPct = size.heightIn * vPctPerIn;
  const { anchor, offsetIn } = rule.vertical;
  let topPct: number;
  if (anchor === 'top') topPct = ART_PHOTO.collarTopPct + offsetIn * vPctPerIn;
  else if (anchor === 'center')
    topPct = ART_PHOTO.collarTopPct + offsetIn * vPctPerIn - heightPct / 2;
  else topPct = 100 - 6 - heightPct; // bottom-anchored (sleeves/hems): up from the near edge
  const centrePct = 50 + viewerSign(location) * rule.horizontal.fromCenterIn * hPctPerIn;
  return {
    leftPct: round1(centrePct - widthPct / 2),
    topPct: round1(topPct),
    widthPct: round1(widthPct),
    guessed: false,
  };
}
const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Where the art sits on the pallet (§ 4.2 "Pallet & placement"): by the seed placement rule when the
 * location has one and the size is known, else the centred fallback the press mode draws. Read-only —
 * the lasers on the press are the truth; this is the picture that says "about here, this big".
 */
export type PalletPlacement = { box: InchBox; rule?: PlacementRule; guessed: boolean };

export function palletPlacement(
  location: string,
  band: 'adult' | 'youth',
  size: DesignSize | undefined,
  platen: Platen,
): PalletPlacement {
  const rule = findPlacementRule(SEED_PLACEMENT_RULES, location, band);
  if (!rule || !size) {
    return {
      box: fallbackBox(platen, size ? size.widthIn / size.heightIn : undefined),
      rule,
      guessed: true,
    };
  }
  return { box: placeDesign(rule, DEFAULT_LOAD_RULE, platen, size), rule, guessed: false };
}

/** "top 4 in below collar · centred" / "… · 3.75 in toward the wearer's left" / a guessed placement. */
export function placementCaption(p: PalletPlacement): string {
  if (p.guessed || !p.rule)
    return 'placement by the default rule — no size or no rule for this location';
  const v =
    p.rule.vertical.anchor === 'bottom'
      ? `bottom ${fmtIn(p.rule.vertical.offsetIn)} in above the hem edge`
      : `${p.rule.vertical.anchor === 'top' ? 'top' : 'centre'} ${fmtIn(p.rule.vertical.offsetIn)} in below collar`;
  const x = p.rule.horizontal.fromCenterIn;
  const h =
    x === 0
      ? 'centred'
      : `${fmtIn(Math.abs(x))} in toward the wearer’s ${x > 0 ? 'left' : 'right'}`;
  return `${v} · ${h}`;
}
